<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import BottomNavigation from '@/components/BottomNavigation.vue';
import { useAuth } from '@/composables/useAuth';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';
import type { AppRoute } from '@/composables/useNavigation';
import AdminView from '@/views/AdminView.vue';
import ChildHome from '@/views/ChildHome.vue';
import FamilyOverview from '@/views/FamilyOverview.vue';
import FriendView from '@/views/FriendView.vue';
import DrawingPadView from '@/views/DrawingPadView.vue';
import LoginView from '@/views/LoginView.vue';
import MailboxView from '@/views/MailboxView.vue';
import ParentApprovalCenter from '@/views/ParentApprovalCenter.vue';
import ParentSettings from '@/views/ParentSettings.vue';
import PlayRequestView from '@/views/PlayRequestView.vue';
import RegisterView from '@/views/RegisterView.vue';
import SetupAdminView from '@/views/SetupAdminView.vue';
import VoiceMessageView from '@/views/VoiceMessageView.vue';
import VideoMessageView from '@/views/VideoMessageView.vue';

const { navigate, route } = useNavigation();
const { initialize, isAuthenticated, isInitialized, needsAdminSetup, user } = useAuth();
const { loadFamilyData, resetFamilyData } = useFamily();

const authRoutes = ['login', 'register', 'setup'];
const familyRoutes: AppRoute[] = [
  'family',
  'child-home',
  'friend',
  'drawing',
  'voice',
  'video',
  'play-request',
  'mailbox',
  'approvals',
  'parent-settings'
];
const isAuthRoute = computed(() => authRoutes.includes(route.value));

onMounted(() => {
  void initialize();
});

watch(
  [route, isAuthenticated, isInitialized, needsAdminSetup, user],
  () => {
    if (!isInitialized.value) return;
    if (needsAdminSetup.value && route.value !== 'setup') {
      navigate('setup');
      return;
    }
    if (!needsAdminSetup.value && !isAuthenticated.value && !isAuthRoute.value) {
      navigate('login');
      return;
    }
    if (isAuthenticated.value && route.value === 'admin' && user.value?.role !== 'admin') {
      navigate('family');
      return;
    }
    if (isAuthenticated.value && user.value?.role === 'admin' && familyRoutes.includes(route.value)) {
      navigate('admin');
      return;
    }
    if (isAuthenticated.value && user.value?.role === 'parent') {
      void loadFamilyData(user.value.familyId);
    } else {
      resetFamilyData();
    }
    if (isAuthenticated.value && isAuthRoute.value) {
      navigate(user.value?.role === 'admin' ? 'admin' : 'family');
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="min-h-screen bg-heart-cream">
    <AppHeader v-if="!isAuthRoute" />
    <main>
      <section v-if="!isInitialized" class="mx-auto max-w-4xl px-4 py-16 text-center md:px-6">
        <div class="rounded-[30px] bg-white p-8 shadow-soft">
          <p class="text-2xl font-black text-heart-berry">Freundedorf wird vorbereitet...</p>
        </div>
      </section>
      <SetupAdminView v-else-if="route === 'setup'" />
      <LoginView v-else-if="route === 'login'" />
      <RegisterView v-else-if="route === 'register'" />
      <AdminView v-else-if="route === 'admin'" />
      <FamilyOverview v-else-if="route === 'family'" />
      <ChildHome v-else-if="route === 'child-home'" />
      <FriendView v-else-if="route === 'friend'" />
      <DrawingPadView v-else-if="route === 'drawing'" />
      <VoiceMessageView v-else-if="route === 'voice'" />
      <VideoMessageView v-else-if="route === 'video'" />
      <PlayRequestView v-else-if="route === 'play-request'" />
      <MailboxView v-else-if="route === 'mailbox'" />
      <ParentApprovalCenter v-else-if="route === 'approvals'" />
      <ParentSettings v-else-if="route === 'parent-settings'" />
    </main>
    <BottomNavigation v-if="!isAuthRoute" />
  </div>
</template>
